import * as React from 'react';
import { useMemo, useState } from 'react';

import { Card, CardContent, VehicleProfileIcon } from '@klareospace/pharos-lib-web-utils';

import type { CarrierOption, TemplateOption } from '../components';
import { VehicleDrawer, VehiclesActionBar, VehiclesTable } from '../components';
import { useVehiclesPage } from '../hooks';
import { useCarriers } from '../../carriers/hooks';
import { useVehicleTemplates } from '../../vehicle-templates/hooks';
import type { VehicleScope } from '../types/vehicle.types';

// ─── Constants ────────────────────────────────────────────────────────────────

const CARRIERS_PAGE_SIZE = 100;
const TEMPLATES_PAGE_SIZE = 100;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface VehiclesPageProps {
  carrierId?: string;
  scope: VehicleScope;
  siteId?: string;
  spaceId?: string;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function VehiclesPage({ carrierId, scope, siteId, spaceId }: VehiclesPageProps): React.JSX.Element {
  const pageState = useVehiclesPage({ carrierId, enabled: true, siteId, spaceId });

  const { data: carriersData } = useCarriers({
    page: 1,
    pageSize: CARRIERS_PAGE_SIZE,
    spaceId: scope === 'space' ? spaceId : undefined,
  });

  const { data: templatesData } = useVehicleTemplates({
    limit: TEMPLATES_PAGE_SIZE,
    page: 1,
    statuses: ['active'],
  });

  const carrierOptions = useMemo<CarrierOption[]>(
    () => (carriersData?.items ?? []).map((carrier) => ({ label: carrier.name, value: carrier.id })),
    [carriersData],
  );

  const templateOptions = useMemo<TemplateOption[]>(
    () =>
      (templatesData?.items ?? []).map((tmpl) => ({
        icon: ({ className }: { className?: string }): React.JSX.Element => (
          <VehicleProfileIcon className={className} profile={tmpl.profile} />
        ),
        label: tmpl.name,
        value: tmpl.id,
      })),
    [templatesData],
  );

  const [drawerOpen, setDrawerOpen] = useState(false);

  function handleAdd(): void {
    setDrawerOpen(true);
  }

  function handleDrawerClose(): void {
    setDrawerOpen(false);
  }

  if (pageState.error) {
    throw pageState.error;
  }

  return (
    <div className="flex h-full flex-1 flex-col gap-4">
      <VehiclesActionBar
        carrierFilter={scope === 'space' ? pageState.carrierFilter : undefined}
        carriers={scope === 'space' ? carrierOptions : undefined}
        onAdd={handleAdd}
        onCarrierFilterChange={scope === 'space' ? pageState.onCarrierFilterChange : undefined}
        onSearchChange={pageState.onSearchChange}
        onStatusFilterChange={pageState.onStatusFilterChange}
        onTemplateFilterChange={pageState.onTemplateFilterChange}
        searchValue={pageState.searchValue}
        statusFilter={pageState.statusFilter}
        templateFilter={pageState.templateFilter}
        templates={templateOptions}
      />
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl shadow-sm">
        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          <VehiclesTable
            carrierId={carrierId}
            isLoading={pageState.isLoading}
            pagination={{
              currentPage: pageState.currentPage,
              onPageChange: pageState.onPageChange,
              pageSize: pageState.pageSize,
              totalItems: pageState.totalItems,
              totalPages: pageState.totalPages,
            }}
            scope={scope}
            siteId={siteId}
            sorting={{
              onSort: pageState.onSort,
              sortBy: pageState.sortBy,
              sortDirection: pageState.sortDirection,
            }}
            spaceId={spaceId}
            vehicles={pageState.vehicles}
          />
        </CardContent>
      </Card>
      <VehicleDrawer
        carrierId={carrierId}
        onClose={handleDrawerClose}
        open={drawerOpen}
        scope={scope}
        siteId={siteId}
        spaceId={spaceId}
        vehicle={null}
      />
    </div>
  );
}
